/**
 * Versie-historie dialoog voor concepten en relaties.
 *
 * Toont alle versies van het geselecteerde object (nieuwste eerst).
 * Per versie: versienummer, wie/wanneer, change-reason. Klik op een
 * versie om een diff met de huidige versie te tonen — werd dít veld
 * gewijzigd, en wat stond er voorheen?
 *
 * "Herstel deze versie" schrijft een nieuwe versie-rij die identiek
 * is aan de gekozen oudere versie (alleen `version` opgehoogd). Geen
 * rewind van de history-counter — auditeerbaarheid behouden.
 */
import type { BilingualText, Concept, Relation } from "@ae/domain";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type VersionEntry } from "../../api/client";
import { resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useWorkspaceData } from "../../state/workspace";

interface ConceptHistoryProps {
  kind: "concept";
  conceptId: string;
  open: boolean;
  onClose: () => void;
}

interface RelationHistoryProps {
  kind: "relation";
  relationId: string;
  open: boolean;
  onClose: () => void;
}

type Props = ConceptHistoryProps | RelationHistoryProps;

export function HistoryDialog(props: Props) {
  const { open, onClose } = props;
  const { t } = useTranslation();
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  // Alleen kijken: de geschiedenis inzien mag, een versie terugzetten niet.
  const readOnly = useSession((s) => s.readOnly);

  const [versions, setVersions] = useState<VersionEntry<Concept | Relation>[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!workspaceId || !branchId) return;
    setError(null);
    try {
      if (props.kind === "concept") {
        const res = await api.listConceptHistory(workspaceId, branchId, props.conceptId);
        setVersions(res.versions);
      } else {
        const res = await api.listRelationHistory(workspaceId, branchId, props.relationId);
        setVersions(res.versions);
      }
    } catch (err) {
      setError(explain(err, t));
    }
  }, [workspaceId, branchId, props, t]);

  useEffect(() => {
    if (open) {
      void refresh();
      setInfo(null);
      setSelectedVersion(null);
    }
  }, [open, refresh]);

  // Esc sluit het venster.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const current = versions[0] ?? null;
  const selected = useMemo(
    () =>
      selectedVersion !== null
        ? (versions.find((v) => v.version_number === selectedVersion) ?? null)
        : null,
    [versions, selectedVersion],
  );

  const restore = async (versionNumber: number) => {
    if (!workspaceId || !branchId) return;
    if (current && versionNumber === current.version_number) return;
    if (!confirm(t("history.restoreConfirm", { version: versionNumber }))) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (props.kind === "concept") {
        await api.restoreConceptVersion(workspaceId, branchId, props.conceptId, versionNumber);
      } else {
        await api.restoreRelationVersion(workspaceId, branchId, props.relationId, versionNumber);
      }
      // Lokale store herhydrateren zodat de canvas + attribute-panel de
      // nieuwe waarden meteen tonen — anders blijft de oude versie te
      // zien tot een volgende mutatie of full reload.
      await useWorkspaceData.getState().hydrate(workspaceId, branchId);
      setInfo(
        t("history.restored", {
          version: versionNumber,
          newVersion: (current?.version_number ?? 0) + 1,
        }),
      );
      await refresh();
    } catch (err) {
      setError(explain(err, t));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="history-title"
    >
      <div
        className="flex h-full w-full max-w-5xl flex-col rounded-lg border border-(--ld-control) bg-(--ld-surface) shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex shrink-0 items-center justify-between border-(--ld-border) border-b px-4 py-2">
          <h2 id="history-title" className="font-semibold text-(--ld-text) text-sm">
            {props.kind === "concept" ? t("history.titleConcept") : t("history.titleRelation")}
          </h2>
          <button
            type="button"
            className="rounded px-2 py-0.5 text-(--ld-muted) text-sm hover:bg-(--ld-raised) hover:text-(--ld-text)"
            onClick={onClose}
            aria-label={t("history.close")}
          >
            ✕
          </button>
        </header>

        <div className="grid min-h-0 flex-1 grid-cols-[280px_1fr] overflow-hidden">
          <aside className="overflow-y-auto border-(--ld-border) border-r bg-(--ld-raised)">
            {versions.length === 0 ? (
              <p className="px-3 py-4 text-(--ld-muted) text-sm italic">
                {t("history.noVersions")}
              </p>
            ) : (
              <ul className="divide-y divide-(--ld-rule)">
                {versions.map((v) => (
                  <li key={v.version_number}>
                    <button
                      type="button"
                      onClick={() => setSelectedVersion(v.version_number)}
                      className={`flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-(--ld-raised) ${
                        selectedVersion === v.version_number
                          ? "bg-(--ld-active) text-(--ld-accent)"
                          : "text-(--ld-text)"
                      }`}
                    >
                      <div className="flex w-full items-center justify-between">
                        <span className="font-mono font-semibold">v{v.version_number}</span>
                        {current?.version_number === v.version_number && (
                          <span className="rounded bg-(--ld-positive-bg) px-1.5 py-0.5 text-(--ld-positive-ink) text-xs">
                            {t("history.current")}
                          </span>
                        )}
                      </div>
                      <span className="text-(--ld-muted) text-xs">
                        {formatTimestamp(v.created_at)}
                      </span>
                      <span className="font-mono text-(--ld-muted) text-xs">{v.created_by}</span>
                      {v.change_reason && (
                        <span className="text-(--ld-muted) text-xs italic">
                          {prettyReason(v.change_reason, t)}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          <section className="flex min-h-0 flex-col overflow-hidden">
            <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
              {error && (
                <p className="rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
                  {error}
                </p>
              )}
              {info && (
                <p className="rounded border border-transparent bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm">
                  {info}
                </p>
              )}

              {selected && current ? (
                <DiffView
                  kind={props.kind}
                  current={current.snapshot}
                  selected={selected.snapshot}
                />
              ) : (
                <p className="text-(--ld-muted) text-sm italic">{t("history.pickVersionHint")}</p>
              )}
            </div>

            {selected &&
              current &&
              selected.version_number !== current.version_number &&
              !readOnly && (
                <footer className="flex shrink-0 items-center justify-end gap-2 border-(--ld-border) border-t bg-(--ld-raised) px-4 py-2">
                  <button
                    type="button"
                    className="rounded bg-(--ld-primary) px-3 py-1.5 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
                    onClick={() => void restore(selected.version_number)}
                    disabled={busy}
                  >
                    {t("history.restoreButton")}
                  </button>
                </footer>
              )}
          </section>
        </div>
      </div>
    </div>
  );
}

function DiffView({
  kind,
  current,
  selected,
}: {
  kind: "concept" | "relation";
  current: Concept | Relation;
  selected: Concept | Relation;
}) {
  const { t } = useTranslation();
  const fields =
    kind === "concept"
      ? conceptFields(current as Concept, selected as Concept, t)
      : relationFields(current as Relation, selected as Relation, t);
  const changed = fields.filter((f) => f.changed);
  const unchanged = fields.filter((f) => !f.changed);

  return (
    <div className="flex flex-col gap-3">
      <h3 className="font-semibold text-(--ld-text) text-sm">{t("history.diffHeading")}</h3>
      {changed.length === 0 ? (
        <p className="rounded bg-(--ld-raised) px-3 py-2 text-(--ld-muted) text-sm italic">
          {t("history.noDifferences")}
        </p>
      ) : (
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-(--ld-border) border-b text-left text-(--ld-muted)">
              <th className="py-1.5 pr-3">{t("history.colField")}</th>
              <th className="py-1.5 pr-3">{t("history.colBecomes")}</th>
              <th className="py-1.5 pr-3">{t("history.colWas")}</th>
            </tr>
          </thead>
          <tbody>
            {changed.map((f) => (
              <tr key={f.label} className="border-(--ld-rule) border-b align-top">
                <td className="py-1.5 pr-3 font-medium text-(--ld-text)">{f.label}</td>
                <td className="py-1.5 pr-3">
                  <pre className="whitespace-pre-wrap break-words font-mono text-(--ld-positive-ink)">
                    {formatValue(f.selectedValue, t)}
                  </pre>
                </td>
                <td className="py-1.5 pr-3">
                  <pre className="whitespace-pre-wrap break-words font-mono text-(--ld-muted)">
                    {formatValue(f.currentValue, t)}
                  </pre>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {unchanged.length > 0 && (
        <details className="text-(--ld-muted) text-xs">
          <summary className="cursor-pointer hover:text-(--ld-text)">
            {t("history.showUnchanged", { n: unchanged.length })}
          </summary>
          <ul className="mt-1 list-disc pl-5">
            {unchanged.map((f) => (
              <li key={f.label}>{f.label}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

interface FieldDiff {
  label: string;
  currentValue: unknown;
  selectedValue: unknown;
  changed: boolean;
}

function conceptFields(current: Concept, selected: Concept, t: TFunction): FieldDiff[] {
  const fields: Array<[string, unknown, unknown]> = [
    [t("attributePanel.nameNl"), current.name?.nl, selected.name?.nl],
    [t("attributePanel.nameEn"), current.name?.en, selected.name?.en],
    [t("attributePanel.sectionType"), current.typeId, selected.typeId],
    [t("attributePanel.descriptionNl"), current.description?.nl, selected.description?.nl],
    [t("attributePanel.descriptionEn"), current.description?.en, selected.description?.en],
    [t("attributePanel.sectionOwner"), current.ownerEmail, selected.ownerEmail],
    [t("history.fields.parent"), current.parentId, selected.parentId],
    [t("history.fields.tags"), current.tags, selected.tags],
    [t("attributePanel.sectionAttributes"), current.attributes, selected.attributes],
    [
      t("history.fields.deleted"),
      current.tombstoneInBranch !== null,
      selected.tombstoneInBranch !== null,
    ],
  ];
  return fields.map(([label, currentValue, selectedValue]) => ({
    label,
    currentValue,
    selectedValue,
    changed: !deepEqual(currentValue, selectedValue),
  }));
}

function relationFields(current: Relation, selected: Relation, t: TFunction): FieldDiff[] {
  const fields: Array<[string, unknown, unknown]> = [
    [t("attributePanel.sectionType"), current.typeId, selected.typeId],
    [t("history.fields.source"), current.sourceConceptId, selected.sourceConceptId],
    [t("history.fields.target"), current.targetConceptId, selected.targetConceptId],
    [t("attributePanel.labelNl"), current.label?.nl, selected.label?.nl],
    [t("attributePanel.labelEn"), current.label?.en, selected.label?.en],
    [t("attributePanel.sectionOwner"), current.ownerEmail, selected.ownerEmail],
    [t("attributePanel.sectionAttributes"), current.attributes, selected.attributes],
    [
      t("history.fields.deleted"),
      current.tombstoneInBranch !== null,
      selected.tombstoneInBranch !== null,
    ],
  ];
  return fields.map(([label, currentValue, selectedValue]) => ({
    label,
    currentValue,
    selectedValue,
    changed: !deepEqual(currentValue, selectedValue),
  }));
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return false;
  // Snel-en-vies: JSON-stringify met gesorteerde keys voor stabiele
  // vergelijking. Voor onze scope (kleine objects met scalairen,
  // arrays, en bilingual maps) is dit voldoende.
  return canonicalJson(a) === canonicalJson(b);
}

function canonicalJson(value: unknown): string {
  return JSON.stringify(value, (_key, v) => {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      const sorted: Record<string, unknown> = {};
      for (const k of Object.keys(v as Record<string, unknown>).sort()) {
        sorted[k] = (v as Record<string, unknown>)[k];
      }
      return sorted;
    }
    return v;
  });
}

function formatValue(value: unknown, t: TFunction): string {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value || t("history.valueEmpty");
  if (typeof value === "boolean") return value ? t("history.valueYes") : t("history.valueNo");
  if (Array.isArray(value)) {
    if (value.length === 0) return t("history.valueNone");
    return value.map((v) => (typeof v === "string" ? v : JSON.stringify(v))).join(", ");
  }
  if (isBilingualText(value)) {
    return resolveBilingual(value, "nl") || t("history.valueEmpty");
  }
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function isBilingualText(v: unknown): v is BilingualText {
  if (!v || typeof v !== "object" || Array.isArray(v)) return false;
  const keys = Object.keys(v);
  return keys.length > 0 && keys.every((k) => k === "nl" || k === "en" || k.length === 2);
}

function formatTimestamp(ts: string): string {
  // SQLite timestamps zijn 'YYYY-MM-DD HH:MM:SS' (UTC). Voeg Z toe.
  const iso = ts.includes("T") ? ts : `${ts.replace(" ", "T")}Z`;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return ts;
  return new Date(ms).toLocaleString();
}

function prettyReason(reason: string, t: TFunction): string {
  if (reason === "create") return t("history.reason.create");
  if (reason === "update") return t("history.reason.update");
  if (reason === "restore") return t("history.reason.restore");
  if (reason.startsWith("restore-from-v")) {
    return t("history.reason.restoreFromVersion", {
      version: reason.replace("restore-from-v", ""),
    });
  }
  return reason;
}

function explain(err: unknown, t: TFunction): string {
  if (err instanceof ApiError) {
    return err.body?.detail ?? err.body?.title ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return t("history.unknownError");
}
